import moment from "moment/moment";
import { Storage } from "aws-amplify";
import { v4 as uuidv4 } from "uuid";

export const MAX_FILE_AMOUNT = 50;
export const MAX_FILE_SIZE = 300;

export const appendDateStampToFileName = (fileName: string) => {
    return `${uuidv4()}/${fileName}`;
};

export const toMB = (num: number) => {
    return Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2,
    }).format(num / 1024 ** 2);
};

export const calculateSpeedInKB = file => {
    const speed = Math.ceil(
        (file?.totalUploaded ?? 0) /
            moment().utc(true).diff(moment(file?.uploadStartTime).utc(true), "seconds") /
            1024
    );

    return speed != Infinity && !isNaN(speed) ? `${speed}KB/s` : "";
};

export const getDocumentType = (type: string) => {
    switch (type) {
        case "application/pdf":
            return "pdf";
        case "image/png":
            return "png";
        case "image/jpeg":
            return "jpeg";
        case "image/jpg":
            return "jpg";
        case "text/csv":
            return "csv";
        case "application/vnd.ms-excel":
            return "xlsx";
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            return "xlsx";
        default:
            return "";
    }
};

export const triggerAuditAPI = async (
    file,
    setAuditGenerated,
    auditGenerated,
    triggerAudit,
    alias,
    useCase,
    s3Prefix,
    dataSourceType = null,
    country = null,
    dataType = null
) => {
    setAuditGenerated(
        new Map(
            auditGenerated.set(file.uniqueName, {
                ...auditGenerated.get(file.uniqueName),
                isLoading: true,
            })
        )
    );

    interface AuditDetails {
        uploadedBy: string;
        fileName: string;
        fileSizeInMB: number;
        fileType: string;
        uploadTimeStamp: string;
        uploadSource: string;
        s3Key: string;
        dataSourceType?: string;
        country?: string;
        dataType?: string;
    }

    const auditDetails: AuditDetails = {
        uploadedBy: alias,
        fileName: file.uniqueName,
        fileSizeInMB: Number(toMB(file.size)),
        fileType: getDocumentType(file.type),
        uploadTimeStamp: moment().utc(true).toISOString(),
        uploadSource: "UI",
        s3Key: s3Prefix + `${file.uniqueName}`,
    };

    if (dataSourceType !== null) {
        auditDetails.dataSourceType = dataSourceType;
    }

    if (country !== null) {
        auditDetails.country = country;
    }

    if (dataType !== null) {
        auditDetails.dataType = dataType;
    }

    try {
        const result = await triggerAudit({
            businessUseCase: "SMART",
            useCase: useCase,
            auditDetails: auditDetails,
        });

        if (result?.data?.status === "SUCCEEDED") {
            setAuditGenerated(
                new Map(
                    auditGenerated.set(file.uniqueName, {
                        ...auditGenerated.get(file.uniqueName),
                        auditGenerated: true,
                        isLoading: false,
                    })
                )
            );

            return result.data; // Return the actual response data
        } else {
            throw new Error("Unexpected error");
        }
    } catch (error) {
        throw new Error(error);
    }
};

export const uploadFileToS3 = async (
    file,
    setAuditGenerated,
    auditGenerated,
    triggerAudit,
    alias,
    useCase,
    s3Prefix,
    dataSourceType = null,
    country = null,
    dataType = null
) => {
    try {
        // Upload file to S3
        const response = await Storage.put(s3Prefix + file.uniqueName, file, {
            progressCallback(progress) {
                setAuditGenerated(
                    new Map(
                        auditGenerated.set(file.uniqueName, {
                            ...(!auditGenerated.has(file.uniqueName)
                                ? { uploadStartTime: moment().utc(true) }
                                : null),
                            ...auditGenerated.get(file.uniqueName),
                            auditGenerated: false,
                            isLoading: false,
                            isError: false,
                            totalUploaded: progress.loaded,
                            progress: Math.floor((progress.loaded / progress.total) * 100),
                        })
                    )
                );
            },
        });

        if (response.key) {
            // Trigger audit API and wait for response
            const auditResponse = await triggerAuditAPI(
                file,
                setAuditGenerated,
                auditGenerated,
                triggerAudit,
                alias,
                useCase,
                s3Prefix,
                dataSourceType,
                country,
                dataType
            );

            return auditResponse;
        } else {
            throw new Error();
        }
    } catch (error) {
        throw new Error(error);
    }
};

export const uploadFileAndGenerateAudit = async (
    file,
    setIsDisabled,
    setAuditGenerated,
    auditGenerated,
    triggerAudit,
    triggerIngest,
    alias,
    useCase,
    s3Prefix,
    dataSourceType = null,
    country = null,
    dataType = null
) => {
    try {
        setIsDisabled(true);
        const auditResponse = await uploadFileToS3(
            file,
            setAuditGenerated,
            auditGenerated,
            triggerAudit,
            alias,
            useCase,
            s3Prefix,
            dataSourceType,
            country,
            dataType
        );

        // If useCase is DATA_SOURCE_FILE_UPLOAD, call ingestDataSourceFile
        if (useCase === "DATA_SOURCE_FILE_UPLOAD" && auditResponse) {
            try {
                // Transform dataType for CBRE
                let apiDataType = dataType || "";
                if (dataSourceType === "CBRE") {
                    if (dataType === "VacancyRate") {
                        apiDataType = "VACANCY";
                    } else if (dataType === "CapRate") {
                        apiDataType = "CAP";
                    }
                }

                const ingestRequest = {
                    country: country || "",
                    dataSourceType: (dataSourceType || "").toUpperCase(),
                    dataS3Key: [`${s3Prefix}${file.uniqueName}`],
                    fileType: getDocumentType(file.type),
                    businessUseCase: "SMART",
                    content: {
                        dataType: apiDataType,
                        eventId: auditResponse.content.eventId,
                        itemTimeStamp: auditResponse.content.itemTimeStamp,
                    },
                };

                console.log("Ingest Request:", ingestRequest);

                triggerIngest(ingestRequest);
            } catch (ingestError) {
                console.error("Ingest Error:", ingestError);
            }
        }
    } catch (error) {
        console.error("Error:", error);
        setAuditGenerated(
            new Map(
                auditGenerated.set(file.uniqueName, {
                    ...auditGenerated.get(file.uniqueName),
                    auditGenerated: false,
                    isLoading: false,
                    isError: true,
                })
            )
        );
    }
};

export const getPresignedUrl = async s3ObjectId => {
    try {
        return await Storage.get(s3ObjectId, { expires: 300, cacheControl: "no-cache" }); // URL expires in 300 seconds
    } catch (error) {
        console.error("Error getting presigned URL:", error);
    }
};
